import React, { useEffect, useState, useContext } from "react";
import { useHistory, useLocation, withRouter } from "react-router-dom";
import { UserContext } from "src/scenes/App/UserContext";
import { BusinessPortalHeader, FlexContainer, PortalPageShell, StandardModal, Toast } from "src/components";
import Sidebar from "./components/Sibebar";
import NewCampaign from "./components/new/NewCampaign";
import { EmailCampaignsContext } from "./EmailCampaignsContext";
import { SIDEBAR_LIST, DOWNGRADE_STEPS, DOWNGRADE_STATUS } from "./constants";

import EmailCampaignsTable from "./table/emails/Table";
import EmailCampaignsSettings from "./components/Settings";
import CustomAudienceTable from "./table/audience/AudienceTable";
import NewAudience from "./components/audience/NewAudience";
import AddSingleContactForm from "./components/audience/AddSingleContactForm";
import EmailStatistics from "./components/EmailStatistics";
import EmailPro from "./components/EmailPro";
import EmailUpgradeModal from "./components/EmailUpgradeModal";
import EmailUpgradeSuccessModal from "./components/EmailUpgradeSuccessModal";
import { getSettings } from "./api/settingsApi";
import { deleteEmail } from "./api/emailCampaignsApi";
import EmailDowngradeModal from "./components/EmailDowngradeModal";
import DowngradePlanDisplayModal from "./components/DowngradePlanDisplayModal";
import EmailCancelDowngradeModal from "./components/EmailCancelDowngradeModal";

function EmailCampaigns() {

    const user = useContext(UserContext);
    const location = useLocation();
    const history = useHistory();

    const [createNewCampaign, setCreateNewCampaign] = useState(false);
    const [createNewAudience, setCreateNewAudience] = useState(false);
    const [showAddSingleContact, setShowAddSingleContact] = useState(false);
    const [currentSidebar, setCurrentSidebar] = useState(SIDEBAR_LIST.SENT);
    const [uploadedCsvData, setUploadedCsvData] = useState([]);
    const [audienceName, setAudienceName] = useState("");
    const [showEmailPackages, setShowEmailPackages] = useState(false);
    const [initialSettings, setInitialSettings] = useState(null);
    const [initialStatistics, setInitialStatistics] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteIsLoading, setDeleteIsLoading] = useState(false);
    const [campaignUuidToDelete, setCampaignUuidToDelete] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedEmailsToDelete, setSelectedEmailsToDelete] = useState([]);

    const [packages, setPackages] = useState([]);
    const [isPaymentOpen, setIsPaymentOpen] = useState(false);
    const [paymentProps, setPaymentProps] = useState(null);
    const [purchasedEmailPro, setPurchasedEmailPro] = useState(null);
    const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
    const [proratePricing, setProratePricing] = useState(null);

    const [isDowngradeOpen, setIsDowngradeOpen] = useState(false);
    const [downgradeProps, setDowngradeProps] = useState(null);
    const [downgradeStep, setDowngradeStep] = useState(DOWNGRADE_STEPS.SELECT);

    const [pendingDowngrade, setPendingDowngrade] = useState(null);
    const [showCancelDowngradeSuccess, setShowCancelDowngradeSuccess] = useState(false);

    const [contactFormData, setContactFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        reference: "",
    });

    const startNewEmail = () => {
        setCreateNewCampaign(true);
        setSelectedCampaign(null);
    };

    const startUpdatingEmail = emailCampaign => {
        setCreateNewCampaign(true);
        setSelectedCampaign(emailCampaign);
    };

    const closeEmailForm = () => {
        setCreateNewCampaign(false);
        setSelectedCampaign(null);
    };

    const startDeletingCampaign = emailCampaign => {
        setShowDeleteDialog(true);
        setCampaignUuidToDelete(emailCampaign.uuid);
    };

    const dismissDeleteDialog = () => {
        setShowDeleteDialog(false);
        setCampaignUuidToDelete(null);
    };

    const openCreateNewAudience = () => setCreateNewAudience(!createNewAudience);
    const toggleEmailPackages = () => {
        setShowEmailPackages(!showEmailPackages);
        setShowCancelDowngradeSuccess(false);
        if (!showEmailPackages) {
            setIsDowngradeOpen(false);
            setDowngradeProps(null);
            setDowngradeStep(DOWNGRADE_STEPS.SELECT);
        }
    };

    const cancelCreateNewAudience = () => {
        setCreateNewAudience(false);
        setUploadedCsvData([]);
        setAudienceName("");
    };
    const toggleAddSingleContact = () => {
        setCreateNewAudience(!createNewAudience);
        setShowAddSingleContact(!showAddSingleContact);
    };

    const openPayment = ({ plan, currentAddon }) => {
        setPaymentProps({
            plan,
            currentAddon,
            statistics: initialStatistics,
            settings: initialSettings
        });
        setIsPaymentOpen(true);
        setShowEmailPackages(false);
    };

    const closePayment = () => {
        setPaymentProps(null);
        setIsPaymentOpen(false);
        setShowUpgradeSuccess(false);
        initialFetchSettings();
    };

    const backToEmailPro = () => {
        setIsPaymentOpen(false);
        setPaymentProps(null);
        setShowEmailPackages(true);

        setIsDowngradeOpen(false);
        setDowngradeProps(null);
        setDowngradeStep(DOWNGRADE_STEPS.SELECT);
    };

    const closeDowngradeModal = () => {
        setIsDowngradeOpen(false);
        setDowngradeProps(null);
        setDowngradeStep(DOWNGRADE_STEPS.SELECT);
        setShowEmailPackages(false);
    };

    const openDowngrade = ({ plan, currentAddon }) => {
        const isPending = pendingDowngrade?.newAddonUuid === plan.uuid;
        setDowngradeProps({
            currentPlan: currentAddon,
            downgradePlan: plan,
            effectiveFrom: initialStatistics?.proratedPeriod?.endsAt,
            status: isPending ? DOWNGRADE_STATUS.CANCEL : DOWNGRADE_STATUS.DOWNGRADE,
        });
        setDowngradeStep(DOWNGRADE_STEPS.SELECT);
        setIsDowngradeOpen(true);
        setShowEmailPackages(false);
    };

    const openCancelDowngradeSuccess = async () => {
        try {
            await initialFetchSettings();
            setIsDowngradeOpen(false);
            setDowngradeProps(null);
            setDowngradeStep(DOWNGRADE_STEPS.SELECT);
            setShowCancelDowngradeSuccess(true);
        } catch (error) {
            console.error("Cancel downgrade failed:", error);
            Toast.error("Failed to cancel downgrade. Please try again.");
        }
    };

    const openFinalDowngradeModal = (plan) => {
        const planPricing = proratePricing?.find(p => p.addonId === plan.uuid)?.total ?? 0;
        setDowngradeProps(prev => ({
            ...prev,
            downgradePlan: plan,
            price: planPricing
        }));
        setDowngradeStep(DOWNGRADE_STEPS.CONFIRM);
    };

    const sidebarDisplay = () => {
        switch (currentSidebar) {
            case SIDEBAR_LIST.SENT:
                return <EmailCampaignsTable status={SIDEBAR_LIST.SENT} />;
            case SIDEBAR_LIST.SCHEDULED:
                return <EmailCampaignsTable status={SIDEBAR_LIST.SCHEDULED} />;
            case SIDEBAR_LIST.DRAFT:
                return <EmailCampaignsTable status={SIDEBAR_LIST.DRAFT} />;
            case SIDEBAR_LIST.CUSTOM_AUDIENCE:
                return <CustomAudienceTable />;
            case SIDEBAR_LIST.SETTINGS:
                return <EmailCampaignsSettings />;
            default:
                return null;
        }
    };

    const onDeleteEmail = async () => {
        setDeleteIsLoading(true);
        try {
            const response = await deleteEmail(campaignUuidToDelete);
            if (response) {
                Toast.info("Email campaign successfully deleted.");
                dismissDeleteDialog();
            }
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while deleting email campaign.");
        } finally {
            setDeleteIsLoading(false);
        }
    };

    const handleDeleteSelectedCampaigns = async () => {
        const selectedUUIDs = selectedEmailsToDelete.map(email => email.uuid);

        setDeleteIsLoading(true);
        let successCount = 0;
        try {
            for (const uuid of selectedUUIDs) {
                const response = await deleteEmail(uuid);

                if (response.info === "success") {
                    successCount++;
                }
            }
            if (successCount > 0) {
                Toast.success("Email campaigns deleted successfully");
            }
        } catch (error) {
            console.error("Error deleting email campaigns: ", error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setSelectedEmailsToDelete([]);
            setDeleteIsLoading(false);
        }
    };
    const initialFetchSettings = async () => {
        try {
            const response = await getSettings();
            setInitialSettings(response.settings);
            setInitialStatistics(response.statistics);
            setPackages(response?.statistics?.emailProPackages);
            setPurchasedEmailPro(response.emailAddonPurchases);
            setProratePricing(response.proratedPricing);
            setPendingDowngrade(response.pendingEmailCupDowngrade);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while fetching settings.");
        }
    };

    useEffect(() => {
        initialFetchSettings();
    }, []);

    useEffect(() => {
        const emailCampaignsFilter = new URLSearchParams(location.search).get("emailCampaigns");
        if (emailCampaignsFilter !== SIDEBAR_LIST.SCHEDULED) {
            return;
        }

        setCreateNewCampaign(false);
        setShowEmailPackages(false);
        setSelectedEmailsToDelete([]);
        setCurrentSidebar(SIDEBAR_LIST.SCHEDULED);
        history.replace("/email-campaigns");
    }, [location.search]);

    return (
        <EmailCampaignsContext.Provider
            value={{
                user,

                createNewCampaign,
                startNewEmail,
                startUpdatingEmail,
                closeEmailForm,
                currentSidebar,
                setCurrentSidebar,
                createNewAudience,
                setCreateNewAudience,
                openCreateNewAudience,
                toggleAddSingleContact,
                cancelCreateNewAudience,

                uploadedCsvData,
                setUploadedCsvData,
                audienceName,
                setAudienceName,
                toggleEmailPackages,

                initialFetchSettings,
                initialSettings,
                setInitialSettings,
                initialStatistics,
                setInitialStatistics,
                startDeletingCampaign,
                dismissDeleteDialog,
                showDeleteDialog,
                onDeleteEmail,
                deleteIsLoading,

                selectedCampaign,
                setSelectedCampaign,
                selectedEmailsToDelete,
                setSelectedEmailsToDelete,
                handleDeleteSelectedCampaigns,
                openPayment,
                packages,
                purchasedEmailPro,
                proratePricing,
                openDowngrade,
                pendingDowngrade
            }}
        >
            <PortalPageShell
                height={createNewCampaign ? "calc(100vh - 64px - 1rem)" : "auto"}
                overflow={createNewCampaign ? "hidden" : "visible"}
                paddingBottom={createNewCampaign ? "0" : "2rem"}
            >
                <BusinessPortalHeader
                    title="Email Campaigns"
                    description="Create and send professional campaigns to Me Business users or external contacts, manage audiences, schedule sends, and track opens and clicks in real time."
                    margin="1rem 0 1.625rem"
                />
                {createNewCampaign ? (
                    <NewCampaign />
                ) : (
                    <>
                        {initialStatistics &&
                            <EmailStatistics />
                        }
                        <FlexContainer direction="row" tDirection="column" gap="14px">
                            <Sidebar />
                            {sidebarDisplay()}
                        </FlexContainer>
                    </>
                )}
            </PortalPageShell>

            {/* Add new audience */}
            {createNewAudience && (
                <NewAudience />
            )}

            {showAddSingleContact && (
                <AddSingleContactForm
                    toggleAddSingleContact={toggleAddSingleContact}
                    contactFormData={contactFormData}
                    setContactFormData={setContactFormData}
                />
            )}

            {/* Email Pro */}
            <StandardModal
                open={showEmailPackages}
                onClose={toggleEmailPackages}
                widthMode="table"
                bodyPadding="0"
                hideCloseButton
            >
                <EmailPro />
            </StandardModal>
            {isPaymentOpen && (
                <EmailUpgradeModal
                    user={user}
                    plan={paymentProps?.plan}
                    statistics={paymentProps?.statistics}
                    onClose={closePayment}
                    onBack={backToEmailPro}
                    onPurchaseSuccess={ async () => {
                        await initialFetchSettings();
                        setIsPaymentOpen(false); 
                        setShowUpgradeSuccess(true);
                    }}
                    proratePricing={proratePricing}
                />
            )}
            {showUpgradeSuccess && (
                <EmailUpgradeSuccessModal
                    company={user.companyName}
                    plan={paymentProps?.plan}
                    onClose={() => closePayment()}
                    statistics={paymentProps?.statistics}
                    proratePricing={proratePricing}
                />
            )}
            {isDowngradeOpen && downgradeStep === DOWNGRADE_STEPS.SELECT && (
                <StandardModal
                    open={true}
                    onClose={backToEmailPro}
                    title={downgradeProps?.status === DOWNGRADE_STATUS.CANCEL ? "Cancel Plan Downgrade" : "Confirm Plan Change"}
                    description={downgradeProps?.status === DOWNGRADE_STATUS.CANCEL
                        ? `Are you sure you want to cancel your downgrade? Your current ${downgradeProps?.currentPlan?.title || "Email"} plan will remain active and renew as normal.`
                        : "You're about to schedule a downgrade for your Email plan."}
                    widthMode="compactWide"
                    hideCloseButton
                >
                    <EmailDowngradeModal
                        onBack={backToEmailPro} 
                        currentPlan={downgradeProps?.currentPlan}
                        downgradePlan={downgradeProps?.downgradePlan}
                        openFinalModal={
                            downgradeProps?.status === DOWNGRADE_STATUS.CANCEL
                                ? openCancelDowngradeSuccess
                                : openFinalDowngradeModal
                        }
                        status={downgradeProps?.status} 
                    />
                </StandardModal>
            )}
            {isDowngradeOpen && downgradeStep === DOWNGRADE_STEPS.CONFIRM && (
                <DowngradePlanDisplayModal
                    downgradePlan={downgradeProps?.downgradePlan}
                    onBack={closeDowngradeModal}
                    purchasedEmailPro={purchasedEmailPro}
                    effectiveDate={downgradeProps?.effectiveFrom}
                    price={downgradeProps?.price}
                    status={downgradeProps?.status}
                    onSuccess={initialFetchSettings}
                />
            )}

            {showCancelDowngradeSuccess && (
                <EmailCancelDowngradeModal
                    onBack={backToEmailPro}
                    downgradePlan={pendingDowngrade?.addon}
                    effectiveDate={pendingDowngrade?.effectiveAt}
                    refreshSettings={initialFetchSettings}
                />
            )}
        </EmailCampaignsContext.Provider>
    );
}

export default withRouter(EmailCampaigns);
